import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import {
  Rss, CreditCard, FolderOpen, FileText, Users, Settings,
  Menu, ChevronDown, LogOut, Building2, Wallet, LifeBuoy, Info, Receipt
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import SubscriptionGate from './SubscriptionGate';
import TrialBanner from './TrialBanner';
import { AnalyticsEvents, track } from '../services/AnalyticsService';
import { GuideNavButton, GuideProvider, useGuide } from '../guide/GuideContext';
import { useDemoSession } from '../demo/DemoSession';
import { PRICING_TIERS } from '../constants/pricing';

function getTierName(subscriptionTier: string | undefined): string {
  if (subscriptionTier === 'free') return 'Provperiod';
  const tier = subscriptionTier ? PRICING_TIERS[subscriptionTier] : undefined;
  return tier ? `${tier.name} – ${tier.price}` : '';
}

const navItems = [
  { to: 'feed', label: 'Anslagstavla', icon: Rss, section: 'feed', boardOnly: false },
  { to: 'payments', label: 'Betalningar', icon: CreditCard, section: 'payments', boardOnly: false },
  { to: 'ekonomi', label: 'Ekonomi', icon: Wallet, section: 'ekonomi', boardOnly: true },
  { to: 'documents', label: 'Dokument', icon: FolderOpen, section: 'documents', boardOnly: false },
  { to: 'proposals', label: 'Förslag', icon: FileText, section: 'proposals', boardOnly: false },
  { to: 'members', label: 'Medlemmar', icon: Users, section: 'members', boardOnly: false },
  { to: 'settings', label: 'Inställningar', icon: Settings, section: 'settings', boardOnly: false },
  { to: 'billing', label: 'Fakturering', icon: Receipt, section: 'billing', boardOnly: true },
  { to: 'support', label: 'Support/Förbättringsförslag', icon: LifeBuoy, section: 'support', boardOnly: false },
];

export default function Layout() {
  return (
    <GuideProvider>
      <LayoutContent />
    </GuideProvider>
  );
}

function LayoutContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { organizations, currentOrg, setCurrentOrgId, notifications, isBoardMember } = useOrganization();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const demo = useDemoSession();
  const { exitDemo } = useGuide();

  useEffect(() => {
    if (orgId) setCurrentOrgId(orgId);
  }, [orgId, setCurrentOrgId]);

  const handleOrgSwitch = (id: string) => {
    setCurrentOrgId(id);
    track(AnalyticsEvents.OrganizationSelected, { orgId: id, source: 'layout' });
    setOrgDropdownOpen(false);
    navigate(`/org/${id}/feed`);
  };

  const handleSignOut = () => {
    signOut();
    navigate('/login');
  };


  const getNotificationCount = (section: string): number => {
    if (!notifications) return 0;
    const map: Record<string, number> = {
      feed: notifications.feed.unreadCount,
      payments: notifications.payments.unreadCount,
      documents: notifications.documents.unreadCount,
      proposals: notifications.proposals.unreadCount,
      members: notifications.members.unreadCount,
    };
    return map[section] ?? 0;
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      <div className="p-4 border-b border-gray-100">
        <div className="relative">
          <button
            onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
          >
            <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {currentOrg?.name ?? 'Välj förening'}
              </p>
              <p className="text-xs text-gray-500">{getTierName(currentOrg?.subscriptionTier)}</p>
            </div>
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          </button>

          {orgDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-64 overflow-y-auto">
              {organizations.map(org => (
                <button
                  key={org.id}
                  onClick={() => handleOrgSwitch(org.id)}
                  className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors first:rounded-t-xl last:rounded-b-xl ${
                    org.id === currentOrg?.id ? 'text-blue-600 font-medium bg-blue-50' : 'text-gray-700'
                  }`}
                >
                  {org.name}
                </button>
              ))}
              <div className="border-t border-gray-100">
                <button
                  onClick={() => { setOrgDropdownOpen(false); demo ? exitDemo() : navigate('/organizations'); }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-500 hover:bg-gray-50 rounded-b-xl"
                >
                  {demo ? 'Avsluta testföreningen' : 'Hantera föreningar'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1">
        {navItems.filter(item => !item.boardOnly || isBoardMember).map(({ to, label, icon: Icon, section }) => {
          const count = getNotificationCount(section);
          return (
            <NavLink
              key={to}
              to={`/org/${orgId ?? currentOrg?.id}/${to}`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              <span className="flex-1">{label}</span>
              {count > 0 && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                  {count > 9 ? '9+' : count}
                </span>
              )}
            </NavLink>
          );
        })}
        <GuideNavButton onClick={() => setSidebarOpen(false)} />
      </nav>

      <div className="p-4 border-t border-gray-100 relative z-10 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-semibold">
              {(user?.fullName || user?.email || '?')[0].toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.fullName || user?.email}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
          <button
            onClick={demo ? exitDemo : handleSignOut}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title={demo ? 'Avsluta testföreningen' : 'Logga ut'}
            aria-label={demo ? 'Avsluta testföreningen' : 'Logga ut'}
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex w-64 flex-shrink-0">
        {sidebarContent}
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <div className="relative w-72 flex flex-col">
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center gap-3 p-4 bg-white border-b border-gray-200">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          <span className="font-semibold text-gray-900">{currentOrg?.name ?? 'Ra'}</span>
        </div>

        <main className="flex-1 overflow-y-auto flex flex-col">
          {demo && (
            <div className="flex items-center gap-2 bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-sm text-amber-900">
              <Info className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1">Du är i Test förening – inget sparas och inga SMS/e-post skickas.</span>
              <button onClick={exitDemo} className="font-medium underline hover:no-underline">Avsluta testföreningen</button>
            </div>
          )}
          <TrialBanner />
          <SubscriptionGate>
            <Outlet />
          </SubscriptionGate>
        </main>
      </div>
    </div>
  );
}
